import type { ControllerViewOptionValuesDto } from 'cadenza/api-client/dto/controller-view-data-dto';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getDataViewById, getViewByDataViewId, getViewById } from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

export const getDataViewToDataViewConnectionStatus = (
  state: RootStateOr<WorkbookState>,
  sourceDataViewId: DataViewId,
) =>
  Object.values(slice(state).dataViewToDataViewConnectionStatus).filter(
    (connnectionStatus) => connnectionStatus.sourceDataViewId === sourceDataViewId,
  );

const getOneDataViewToDataViewConnectionStatus = (
  state: RootStateOr<WorkbookState>,
  sourceDataViewId: DataViewId,
  targetDataViewId: DataViewId,
) =>
  Object.values(slice(state).dataViewToDataViewConnectionStatus).filter(
    (connectionStatus) =>
      connectionStatus.sourceDataViewId === sourceDataViewId && connectionStatus.targetDataViewId === targetDataViewId,
  )[0];

export const isDataViewToDataViewConnectionErroneous = (
  state: RootStateOr<WorkbookState>,
  sourceDataViewId: DataViewId,
  targetDataViewId: DataViewId,
) => getOneDataViewToDataViewConnectionStatus(state, sourceDataViewId, targetDataViewId).error !== undefined;

export const getViewToDataViewConnectionStatus = (state: RootStateOr<WorkbookState>, sourceViewId: ViewId) =>
  Object.values(slice(state).viewToDataViewConnectionStatus).filter(
    (connnectionStatus) => connnectionStatus.sourceViewId === sourceViewId,
  );

export const getOneViewToDataViewConnectionStatus = (
  state: RootStateOr<WorkbookState>,
  sourceViewId: ViewId,
  targetDataViewId: DataViewId,
) =>
  Object.values(slice(state).viewToDataViewConnectionStatus).filter(
    (connectionStatus) =>
      connectionStatus.sourceViewId === sourceViewId && connectionStatus.targetDataViewId === targetDataViewId,
  )[0];

export const getControllerViewValue = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) =>
  slice(state).controllerValueByDataViewId[dataViewId] as ControllerViewOptionValuesDto | undefined;

export const getDataViewConnections = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId).dataViewToDataViewConnections;
};

const getDataViewToDataViewConnectionsByTargetDataViewId = (
  state: RootStateOr<WorkbookState>,
  targetDataViewId: DataViewId,
) => {
  return Object.values(slice(state).dataViewById)
    .flatMap((dataView) => dataView.dataViewToDataViewConnections)
    .filter((con) => con.targetDataViewId === targetDataViewId);
};

export const getControllerConnectionsByTargetDataViewId = (
  state: RootStateOr<WorkbookState>,
  targetDataViewId: DataViewId,
) => {
  return getDataViewToDataViewConnectionsByTargetDataViewId(state, targetDataViewId).filter(
    (con) => con.connectionType === 'CONTROLLER_FILTER',
  );
};

export const isViewControlled = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getViewById(state, viewId).dataViews.some((dataViewId) => isDataViewControlled(state, dataViewId));
};

const isDataViewControlled = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getControllerConnectionsByTargetDataViewId(state, dataViewId).length > 0;
};

export const getViewToDataViewConnectionsByTargetDataViewId = (
  state: RootStateOr<WorkbookState>,
  targetDataViewId: DataViewId,
) => {
  return Object.values(slice(state).viewById)
    .flatMap((view) => view.viewToDataViewConnections)
    .filter((con) => con.targetDataViewId === targetDataViewId);
};

/** This checks all connections the data view is present in (as either source or target). */
export const hasDataViewNoConnectionErrors = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const viewId = getViewByDataViewId(state, dataViewId).id;
  return (
    !getViewToDataViewConnectionStatus(state, viewId).some(({ error }) => Boolean(error)) &&
    !getDataViewToDataViewConnectionStatus(state, dataViewId).some(({ error }) => Boolean(error)) &&
    !getControllingViewsOfDataViewWithStatus(state, dataViewId).some(({ status: { error } }) => Boolean(error))
  );
};

export const getControllingViewsOfDataViewWithStatus = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return [
    ...getDataViewToDataViewConnectionsByTargetDataViewId(state, dataViewId).map((connection) => ({
      status: getOneDataViewToDataViewConnectionStatus(state, connection.sourceDataViewId, connection.targetDataViewId),
      view: getViewByDataViewId(state, connection.sourceDataViewId),
    })),
    ...getViewToDataViewConnectionsByTargetDataViewId(state, dataViewId).map((connection) => ({
      status: getOneViewToDataViewConnectionStatus(state, connection.sourceViewId, connection.targetDataViewId),
      view: getViewById(state, connection.sourceViewId),
    })),
  ];
};

export const isMapViewActivelyFilteringOtherDataViewsByItsExtent = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
) => {
  return (
    getViewById(state, viewId)?.type === 'MAP' &&
    getViewToDataViewConnectionStatus(state, viewId).some((s) => s.status === 'CONNECTED')
  );
};
